import { getDocument } from "pdfjs-dist";
import { useState, useEffect, useRef, useCallback } from "react";
import { StatementParserService } from "./services/StatementParserService";
import { StatementProcessorService } from "./services/StatementProcessorService";
import { ExcelGeneratorService } from "./services/ExcelGeneratorService";
import { EcbExchangeRateService } from "./services/EcbExchangeRateService";
import { 
  Box, 
  Paper, 
  Typography, 
  Button, 
  CircularProgress, 
  IconButton,
  Select,
  MenuItem,
  Alert,
  Snackbar
} from "@mui/material";
import { 
  GetApp as GetAppIcon,
  PictureAsPdf as PdfIcon,
  Close as CloseIcon,
  Download as DownloadIcon
} from "@mui/icons-material";
import { useDropzone } from 'react-dropzone';
import { logEvent } from './utils/analytics';
import { SelectChangeEvent } from '@mui/material';

const MAX_FILE_SIZE = 1024 * 1024; // 1MB in bytes

const PdfReader = () => {
    const [isProcessing, setIsProcessing] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [filesProcessed, setFilesProcessed] = useState(false);
    const [generatedExcelBlobs, setGeneratedExcelBlobs] = useState<{year: number, blob: Blob, fileName?: string}[]>([]);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear() - 1);
    const [error, setError] = useState<string | null>(null);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    // Use useRef to maintain service instances across renders
    const servicesRef = useRef({
        ecbExchangeRateService: new EcbExchangeRateService(),
        statementParserService: new StatementParserService(),
        statementProcessorService: null as StatementProcessorService | null,
        excelGeneratorService: new ExcelGeneratorService()
    });
    
    // Initialize the processor service that depends on the exchange rate service
    useEffect(() => {
        servicesRef.current.statementProcessorService = new StatementProcessorService(
            servicesRef.current.ecbExchangeRateService
        );
    }, []);
    
    // Fetch exchange rates when component mounts
    useEffect(() => {
      const fetchExchangeRates = async () => {
        try {          
          // Get exchange rates for the last 15 years
          const tomorrow = new Date();
          tomorrow.setDate(tomorrow.getDate() + 1);
          const endDate = tomorrow.toISOString().split('T')[0]; // Tomorrow
          const startDate = new Date('2012-01-01')
            .toISOString().split('T')[0];
            
          await servicesRef.current.ecbExchangeRateService.fetchExchangeRates(startDate, endDate);
        } catch (error) {
          console.error("Error fetching exchange rates:", error);
        } finally {
        }
      };
      
      fetchExchangeRates();
    }, []);
    
    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files || files.length === 0) return;
      
      const fileArray = Array.from(files);
      const validFiles = fileArray.filter(file => {
        if (file.size > MAX_FILE_SIZE) {
          alert(`Failas "${file.name}" yra per didelis. Maksimalus dydis yra 1MB`);
          return false;
        }
        return true;
      });
      
      setSelectedFiles(prev => [...prev, ...validFiles]);
      setFilesProcessed(false);
    };

    const handleRemoveFile = (index: number) => {
      logEvent('File Management', 'Remove File', `File index: ${index}`);
      setSelectedFiles(prev => prev.filter((_, i) => i !== index));
      if (selectedFiles.length <= 1) {
        setFilesProcessed(false);
        handleReset();
      }
    };

    const handleProcessFiles = async () => {
      if (selectedFiles.length === 0) return;
      
      logEvent('File Processing', 'Start', `Files: ${selectedFiles.length}`);
      setIsProcessing(true);
      setError(null);
      
      try {
        const statements = [];
        const { 
          statementParserService, 
          statementProcessorService, 
          excelGeneratorService 
        } = servicesRef.current;
        
        // Process each file
        for (let i = 0; i < selectedFiles.length; i++) {
          const file = selectedFiles[i];
          try {
          const reader = new FileReader();
            const arrayBuffer = await new Promise<ArrayBuffer>((resolve, reject) => {
              reader.onload = () => resolve(reader.result as ArrayBuffer);
              reader.onerror = reject;
              reader.readAsArrayBuffer(file);
            });
          
            const pdf = await getDocument({ data: arrayBuffer }).promise;
            const statement = await statementParserService.parseStatement(pdf);
            statements.push(statement);
          } catch (error) {
            // Set error message and show snackbar
            const errorMessage = `Klaida apdorojant failą "${file.name}": ${
              error instanceof Error 
                ? error.message.includes('Invalid statement format')
                  ? 'Neteisingas ataskaitos formatas. Prašome įkelti tinkamą studento finansų ataskaitą.'
                  : error.message.includes('Failed to parse statement')
                  ? 'Nepavyko nuskaityti ataskaitos duomenų. Įsitikinkite, kad failas nėra pažeistas.'
                  : 'Įvyko nenumatyta klaida apdorojant failą.'
                : 'Įvyko nenumatyta klaida.'
            }`;
            setError(errorMessage);
            setOpenSnackbar(true);
            return; // Exit the processing
          }
        }
        
        const balanceChanges = statementProcessorService!.execute(statements, selectedYear);
        
        // Find the earliest and latest years in the balance changes
        let earliestYear = new Date().getFullYear();
        let latestYear = 0;
        
        balanceChanges.forEach(change => {
          const year = new Date(change.getDate()).getFullYear();
          if (year < earliestYear) earliestYear = year;
          if (year > latestYear) latestYear = year;
        });
        
        // Generate a single Excel file with all balance changes
        const workbook = await excelGeneratorService.generateExcel(balanceChanges);
        const buffer = await excelGeneratorService.generateBuffer(workbook);
        
        // Create a blob for the Excel file
        const blob = new Blob([buffer], { 
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        });
        
        // Use the date range in the filename
        const fileName = `paskolos suvestine ${selectedYear}`;
        setGeneratedExcelBlobs([{ year: 0, blob, fileName }]);
        
        logEvent('File Processing', 'Success', `Files processed: ${selectedFiles.length}`);
        setFilesProcessed(true);
      } catch (error) {
        console.error("Error processing files:", error);
        logEvent('File Processing', 'Error', error instanceof Error ? error.message : 'Unknown error');
        setError('Įvyko klaida apdorojant failus. Prašome bandyti dar kartą.');
        setOpenSnackbar(true);
      } finally {
        setIsProcessing(false);
      }
    };

    // Update the download function to handle the complete Excel file
    const handleDownloadExcel = (blob: Blob, year: number, fileName?: string) => {
      if (!blob) return;
      
      logEvent('File Download', 'Excel Download', fileName || `paskolos_suvestine_${year}.xlsx`);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || `paskolos_suvestine_${year}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    };

    const handleReset = () => {
      logEvent('File Management', 'Reset', 'Clear all files');
      setSelectedFiles([]);
      setFilesProcessed(false);
      setGeneratedExcelBlobs([]);
    };

    const onDrop = useCallback((acceptedFiles: File[]) => {
      const validFiles = acceptedFiles.filter(file => {
        if (file.type !== 'application/pdf') {
          alert('Prašome įkelti tik PDF failus');
          logEvent('File Upload', 'Error', 'Invalid file type');
          return false;
        }
        if (file.size > MAX_FILE_SIZE) {
          alert(`Failas "${file.name}" yra per didelis. Maksimalus dydis yra 1MB`);
          logEvent('File Upload', 'Error', 'File too large');
          return false;
        }
        return true;
      });
      
      if (validFiles.length > 0) {
        logEvent('File Upload', 'Success', `Files: ${validFiles.length}`);
        handleFileSelect({ target: { files: validFiles } } as any);
      }
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      onDrop,
      accept: {
        'application/pdf': ['.pdf']
      },
      multiple: true
    });

    const handleCloseSnackbar = () => {
      setOpenSnackbar(false);
    };

    const handleYearChange = (event: SelectChangeEvent<number>) => {
      const newYear = event.target.value as number;
      logEvent('Settings', 'Year Change', `Selected year: ${newYear}`);
      setSelectedYear(newYear);
    };

    return (
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2, width: '100%', maxWidth: 1000, mx: 'auto' }}>
        <Box sx={{ my: 4 }}>
          <Box
            {...getRootProps()}
            sx={{
              border: '2px dashed #cccccc',
              borderRadius: 2,
              p: 8,
              textAlign: 'center',
              backgroundColor: (theme) => theme.palette.grey[50],
              cursor: 'pointer',
              '&:hover': {
                borderColor: (theme) => theme.palette.primary.main,
                backgroundColor: (theme) => theme.palette.grey[100],
              }
            }}
          >
            <Typography variant="h5" gutterBottom>
              Įkelkite visas studentų finansų ataskaitas
            </Typography>
            <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
              Palaikomas formatas: PDF
            </Typography>
            {isDragActive ? (
              <Typography sx={{ mb: 2 }}>Paleiskite failą čia...</Typography>
            ) : (
              <Typography sx={{ mb: 2 }}>Tempkite ir paleiskite PDF failą čia, arba</Typography>
            )}
            <Button variant="contained" component="span">
              Pasirinkti failus
            </Button>
            <input {...getInputProps()} />
          </Box>
          
          {selectedFiles.length > 0 && (
            <Typography variant="subtitle1" gutterBottom>
              Įkelti failai
            </Typography>
          )}
          
          {selectedFiles.length > 0 ? (
            <Box sx={{ mb: 3 }}>
              {selectedFiles.map((file, index) => (
                <Box 
                  key={index} 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    p: 1, 
                    border: '1px solid #eee', 
                    borderRadius: 1,
                    mb: 1
                  }}
                >
                  <Box 
                    sx={{ 
                      bgcolor: '#e8f5e9', 
                      width: 40, 
                      height: 40, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      borderRadius: 1,
                      mr: 2
                    }}
                  >
                    <PdfIcon color="success" />
                  </Box>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body2">{file.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {file.type} • {(file.size / 1024).toFixed(1)} KB
                    </Typography>
                  </Box>
                  <IconButton size="small" onClick={() => handleRemoveFile(index)}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          ) : null}
          
          {/* New Processed Files section */}
          {filesProcessed && generatedExcelBlobs.length > 0 && (
            <>
              <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
                Apdoroti failai
              </Typography>
              <Box sx={{ mb: 3 }}>
                {generatedExcelBlobs.map(({year, blob, fileName}, index) => (
                  <Box 
                    key={index} 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      p: 1, 
                      border: '1px solid #eee', 
                      borderRadius: 1,
                      mb: 1,
                      bgcolor: '#f0f7ff'
                    }}
                  >
                    <Box 
                      sx={{ 
                        bgcolor: '#e3f2fd', 
                        width: 40, 
                        height: 40, 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        borderRadius: 1,
                        mr: 2
                      }}
                    >
                      <DownloadIcon color="primary" />
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body2">{fileName || `paskolos_suvestine_${year}.xlsx`}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Excel lentelė
                      </Typography>
                    </Box>
                    <Button 
                      variant="outlined" 
                      size="small"
                      onClick={handleReset}
                      sx={{ mr: 1 }}
                    >
                      Pradėti iš naujo
                    </Button>
                    <Button 
                      variant="outlined" 
                      size="small" 
                      startIcon={<GetAppIcon />}
                      onClick={() => handleDownloadExcel(blob, year, fileName)}
                    >
                      Atsisiųsti
                    </Button>
                  </Box>
                ))}
              </Box>
            </>
          )}
          
          {selectedFiles.length > 0 && !filesProcessed && (
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                p: 2, 
                border: '1px solid #eee', 
                borderRadius: 1,
                mt: 3,
                bgcolor: 'transparent'
              }}
            >
              {isProcessing ? (
                <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                  <CircularProgress size={24} sx={{ mr: 2 }} />
                  <Typography variant="body2">Apdorojami failai...</Typography>
                </Box>
              ) : (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                    <Typography variant="body2">Apdoroti jūsų failus</Typography>
                  </Box>
                  <Select
                      value={selectedYear}
                      onChange={handleYearChange}
                      size="small"
                      sx={{ height: '32px', mr: 1 }}
                    >
                      {Array.from(
                        { length: 15 }, 
                        (_, i) => new Date().getFullYear() - i
                      ).map((year) => (
                        <MenuItem key={year} value={year}>
                          {year}
                        </MenuItem>
                      ))}
                    </Select>
                  <Button 
                    variant="contained" 
                    onClick={handleProcessFiles}
                    size="small"
                  >
                    Apdoroti failus
                  </Button>
                </>
              )}
            </Box>
          )}
        </Box>
        <Snackbar 
          open={openSnackbar} 
          autoHideDuration={6000} 
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert 
            onClose={handleCloseSnackbar} 
            severity="error" 
            variant="filled"
            sx={{ width: '100%' }}
          >
            {error}
          </Alert>
        </Snackbar>
      </Paper>
    );
};

export default PdfReader;